import { ButtonContent } from '../../ui/ButtonContent';
/** Connected customer storage accounts, using the integrations hub primitives. */
import React, { useCallback, useEffect, useState } from 'react';
import { useOrg } from '../../../lib/OrgProvider';
import { useSupabase } from '../../../lib/SupabaseProvider';
import { captureException } from '../../../lib/telemetry/posthog/frontend';
import { GoogleDriveIcon } from '../../icons/GoogleDriveIcon';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { ConnectedAccountRow } from './integrations/ConnectedAccountRow';
import { ProviderConnectButton } from './integrations/ProviderConnectButton';
import { useIntegrationConnect } from './integrations/useIntegrationConnect';

/** Metadata only. Credentials never enter the browser. */
interface StorageConnection { id: string; provider: string; account_email: string; status: string }
/** Storage-specific OAuth error copy; the shared hook owns navigation and retry state. */
const ERROR_COPY = { scopes_missing: 'Drive access was not granted', declined: 'Connection cancelled', refresh_token_missing: 'Offline access was not granted' };

/** Lists the current user's storage accounts in the active organization. */
export function StorageSection(): React.ReactElement {
  const { activeOrg } = useOrg();
  const { user, supabase } = useSupabase();
  const organizationId = activeOrg?.id;
  const userId = user?.id;
  const { connecting, errorMessage, setErrorMessage, handleConnect } = useIntegrationConnect({ kind: 'storage', errorCopy: ERROR_COPY });
  const [connections, setConnections] = useState<StorageConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [target, setTarget] = useState<StorageConnection | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  /** Reads only organization-scoped public connection metadata. */
  const load = useCallback(async (): Promise<void> => {
    if (!organizationId || !userId) { setConnections([]); setLoading(false); return; }
    setLoading(true); setLoadError(false);
    try {
      const { data, error } = await supabase.from('integration_connections').select('id, provider, account_email, status').eq('integration_type', 'storage').eq('organization_id', organizationId).eq('user_id', userId);
      if (error) throw new Error(error.message);
      setConnections((data ?? []) as StorageConnection[]);
    } catch (error) {
      captureException(error as Error, 'StorageSection.load', { organizationId, userId });
      setLoadError(true);
    } finally { setLoading(false); }
  }, [organizationId, userId, supabase]);
  useEffect(() => { void load(); }, [load]);

  /** Disconnecting removes future access while retaining customer-owned files. */
  const disconnect = async (): Promise<void> => {
    if (!target) return;
    setDisconnecting(true); setErrorMessage(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Session unavailable');
      const response = await fetch(`/api/integrations/connections/${target.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${session.access_token}` } });
      if (!response.ok && response.status !== 404) throw new Error(`Disconnect failed: ${response.status}`);
    } catch (error) {
      captureException(error as Error, 'StorageSection.disconnect', { organizationId, connectionId: target.id });
      setErrorMessage('Couldn’t disconnect. Try again.');
    } finally { setTarget(null); setDisconnecting(false); await load(); }
  };

  return <section aria-label="Google Drive accounts">
    {errorMessage && <p role="alert" className="mb-4 text-sm text-danger">{errorMessage}</p>}
    {loadError && <p role="alert" className="mb-4 flex gap-3 text-sm text-danger">Couldn’t load Drive accounts.<button type="button" className="underline" onClick={() => void load()}>Retry</button></p>}
    {loading ? <div aria-label="Fetching storage accounts" className="h-14 rounded-lg bg-surface animate-pulse motion-reduce:animate-none" /> : <div className="space-y-2">
      {connections.map(connection => <ConnectedAccountRow key={connection.id} icon={<GoogleDriveIcon className="h-5 w-5" />} identity={connection.account_email} secondary="Google Drive" status={connection.status === 'active' ? 'active' : 'attention'} actions={connection.status === 'active' ? <button type="button" className="text-xs text-muted-foreground hover:text-danger" onClick={() => setTarget(connection)}>Disconnect</button> : <button aria-busy={connecting} type="button" disabled={connecting} className="text-xs text-foreground disabled:opacity-50" onClick={() => void handleConnect(connection.provider)}><ButtonContent loading={connecting}>Reconnect</ButtonContent></button>} />)}
      {!connections.some(connection => connection.provider === 'google') && <ProviderConnectButton icon={<GoogleDriveIcon className="h-5 w-5" />} label="Google Drive" busy={connecting} onConnect={() => handleConnect('google')} />}
    </div>}
    <ConfirmDialog open={target !== null} onClose={() => setTarget(null)} onConfirm={() => void disconnect()} title="Disconnect Drive?" description="Existing files stay in Drive." confirmLabel="Disconnect" loading={disconnecting} variant="danger" />
  </section>;
}
